import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { Mail, Inbox } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { toast } from "sonner";
import { formatDate } from "@/lib/api";

const DashboardPage = () => {
  const { user, loading: authLoading } = useAuth();
  const { t } = useLang();
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user && (user.role === "manager" || user.role === "admin")) {
      api
        .get("/inquiries")
        .then((r) => setInquiries(r.data))
        .catch(() => toast.error(t("dash.load_error")))
        .finally(() => setLoading(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  if (authLoading) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "manager" && user.role !== "admin") return <Navigate to="/" replace />;

  return (
    <main data-testid="dashboard-page" className="pt-32 pb-20 min-h-screen bg-background">
      <div className="max-w-5xl mx-auto px-6">
        <div className="mb-10">
          <div className="overline text-accent">{t("dash.overline")}</div>
          <h1 className="font-serif text-4xl md:text-5xl mt-2">{t("dash.hello")}, {user.full_name.split(" ")[0]}.</h1>
          <p className="text-muted-foreground mt-2 max-w-2xl">
            {t("dash.sub")}
          </p>
        </div>

        <div className="bg-card border border-border rounded-2xl p-6 md:p-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-full bg-accent/10 flex items-center justify-center">
              <Inbox size={18} className="text-accent" />
            </div>
            <div>
              <div className="font-serif text-2xl">{t("dash.inbox_title")}</div>
              <div className="text-sm text-muted-foreground">{inquiries.length} {t("dash.messages_received")}</div>
            </div>
          </div>

          {loading ? (
            <div className="text-sm text-muted-foreground py-8 text-center">{t("dash.loading")}</div>
          ) : inquiries.length === 0 ? (
            <div className="text-center py-16">
              <Mail size={40} className="text-muted-foreground mx-auto mb-4" />
              <div className="font-serif text-xl text-foreground">{t("dash.no_requests_title")}</div>
              <div className="text-sm text-muted-foreground mt-2">{t("dash.no_requests_sub")}</div>
            </div>
          ) : (
            <div className="space-y-3">
              {inquiries.map((i) => (
                <div key={i.id} data-testid={`inquiry-${i.id}`} className="border border-border rounded-xl p-5 hover:border-accent/40 transition-colors">
                  <div className="flex items-start justify-between gap-4 mb-3">
                    <div>
                      <div className="font-serif text-xl">{i.name}</div>
                      <div className="text-sm text-muted-foreground">
                        {i.email}
                        {i.phone && <span> · {i.phone}</span>}
                      </div>
                      {i.subject && <div className="overline text-accent mt-2">{i.subject}</div>}
                    </div>
                    <div className="text-xs text-muted-foreground whitespace-nowrap">{formatDate(i.created_at)}</div>
                  </div>
                  <p className="text-foreground/80 leading-relaxed">{i.message}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </main>
  );
};

export default DashboardPage;
